import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCodingRoutes } from '../../hooks/useCodingRoutes';
import { ctGetAssignmentProgress, ctRefreshAssignmentProgress } from '../../services/api';
import {
  Box, Typography, CircularProgress, Alert, Stack,
  Button, Snackbar,
} from '@mui/material';
import GlassCard from '../../components/Cards/GlassCard';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ProgressTable from '../../components/CodingTracker/ProgressTable';
import AppBreadcrumbs from '../../components/AppBreadcrumbs';
import { surfaceGradientSx, gradientTextSx, PRIMARY, SECONDARY } from '../../utils/colors';
import useBatchStore from '../../store/batchStore';

const ProgressPage = () => {
  const { batchId, assignmentId } = useParams();
  const cr = useCodingRoutes();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast]           = useState({ open: false, message: '', severity: 'success' });

  const { currentBatch } = useBatchStore();

  const fetchProgress = () =>
    ctGetAssignmentProgress(assignmentId)
      .then(({ data: res }) => setData(res))
      .catch((e) => setError(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchProgress(); }, [assignmentId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data: summary } = await ctRefreshAssignmentProgress(assignmentId);
      const msg = summary.failed === 0
        ? `Snapshot refreshed — ${summary.succeeded}/${summary.total} students updated.`
        : `Refresh done — ${summary.succeeded} succeeded, ${summary.failed} failed.`;
      setToast({ open: true, message: msg, severity: summary.failed === 0 ? 'success' : 'warning' });
      setLoading(true);
      fetchProgress();
    } catch (err) {
      setToast({
        open:     true,
        message:  err?.response?.data?.message || 'Refresh failed. Please try again.',
        severity: 'error',
      });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );
  if (error) return <Box p={4}><Alert severity="error">{error}</Alert></Box>;

  const { assignment, students, problems, progressMap } = data;

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: 1400, mx: 'auto', minHeight: '100vh' }}>
      <AppBreadcrumbs
        items={[
          { label: 'Coding Tracker', path: cr.trackerHome },
          { label: currentBatch?.name || 'Batch', path: cr.batch(batchId) },
          { label: 'Assignments', path: cr.assignments(batchId) },
          { label: assignment.title, path: cr.assignment(batchId, assignmentId) },
          { label: 'Progress' },
        ]}
      />

      <GlassCard
        hoverable={false}
        sx={{
          p: { xs: 1.5, md: 2 }, mb: 4, borderRadius: 4,
          ...surfaceGradientSx,
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ ...gradientTextSx, mb: 1 }}>
            {assignment.title}
          </Typography>
          {assignment.dueDate && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CalendarTodayRoundedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Due: {new Date(assignment.dueDate).toLocaleDateString('en-IN', {
                  day: '2-digit', month: 'short', year: 'numeric',
                })}
              </Typography>
            </Stack>
          )}
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshRoundedIcon />}
            disabled={refreshing}
            onClick={handleRefresh}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>

          {[
            { icon: <GroupsRoundedIcon sx={{ color: PRIMARY }} />, value: students.length, label: 'Students' },
            { icon: <CodeRoundedIcon sx={{ color: SECONDARY }} />, value: problems.length, label: 'Problems' },
          ].map(({ icon, value, label }) => (
            <GlassCard
              key={label}
              hoverable={false}
              sx={{
                px: 3, py: 2, borderRadius: 3, textAlign: 'center',
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}
            >
              {icon}
              <Box>
                <Typography variant="h6" fontWeight={800} lineHeight={1}>{value}</Typography>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </Box>
            </GlassCard>
          ))}
        </Stack>
      </GlassCard>

      {students.length === 0 || problems.length === 0 ? (
        <GlassCard
          hoverable={false}
          sx={{
            textAlign: 'center', py: 10,
            border: '2px dashed', borderColor: 'divider', borderRadius: 4,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            {students.length === 0 ? 'No students in this batch.' : 'No problems in this assignment.'}
          </Typography>
        </GlassCard>
      ) : (
        <GlassCard
          hoverable={false}
          sx={{ borderRadius: 4, overflow: 'hidden' }}
        >
          <ProgressTable
            students={students}
            problems={problems}
            progressMap={progressMap}
            batchId={batchId}
          />
        </GlassCard>
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProgressPage;
